import React, { useState, useEffect } from 'react'
import Icon from '../icon'
import InputLabel from '../input-label'
import Tooltip from '../tooltip'
import {
  ButtonContainer,
  ButtonUpload,
  IconContainer,
  Hint,
  Label,
  ButtonDownload,
  ButtonsContainer,
} from './styles'

type Props = {
  'data-qa'?: string
  accept?: string | string[]
  disabled?: boolean
  hint?: string
  label?: string
  maxFileSize?: number // in MB
  onSelect: Function
  value?: string
  fileName?: string
  labelDefault?: string
  hintError?: string
  onError?: Function
  isFileRemovable?: boolean
  theme?: TTheme
  onDownloadFile?: (file: string) => void
  onRemove?: Function
}

const FileInput: React.FC<Props> = (props: Props) => {
  const HINT_ERROR = 'Este arquivo não é válido.'
  const LABEL_DEFAULT = 'Escolher arquivo para upload...'
  const EMPTY_VALUE = ''

  const isRemovable =
    props.isFileRemovable === undefined ? true : props.isFileRemovable

  const hiddenFileInput = React.useRef(null)
  const [fileName, setFileName] = useState<string>(
    props.fileName || EMPTY_VALUE,
  )
  const [error, setError] = useState<Error>(null)
  const maxFileSizeInBytes = composeMaxFileSize(props.maxFileSize)
  const theme = composeTheme(Boolean(error), Boolean(fileName))
  const hintText = composeHintText(error, props.hint)
  const iconName = composeIconName(theme, props.disabled)
  const showHint = shouldShowHint(Boolean(error), props.hint)
  const [value, setValue] = useState<string>(props.value || EMPTY_VALUE)
  const dispatchEvent = {
    event: false,
    stopPropagation(isPropagation: boolean) {
      this.event = isPropagation
    },
  }

  const handleChange = (event): void => {
    if (fileName && !isRemovable) return

    setFileName(null)
    setError(null)

    if (event.target?.files?.length === 0) {
      props.onSelect(null)
      return
    }

    const fileUploaded: any = event.target?.files[0]
    const url = URL.createObjectURL(fileUploaded)
    const isValid = shouldValidate(
      fileUploaded,
      maxFileSizeInBytes,
      props.accept,
    )

    if (isValid) {
      setFileName(fileUploaded?.name)
      setValue(url)
      props.onSelect(fileUploaded)
    } else {
      const error = new Error(props.hintError || HINT_ERROR)
      setError(error)

      if (props.onError) {
        props.onError(error)
      }
    }
  }

  const clearInput = (): void => {
    if (value && props.onRemove) {
      const data = {
        value,
        fileName,
      }
      props.onRemove(data)
    }
    dispatchEvent.stopPropagation(true)
    setFileName(null)
    setValue(null)
    props.onSelect(null)
    hiddenFileInput.current.value = EMPTY_VALUE
  }

  useEffect(() => {
    setValue(props.value)
    setFileName(props.fileName)
  }, [props.value, props.fileName])

  return (
    <>
      {props.label && <InputLabel>{props.label}</InputLabel>}

      <ButtonContainer>
        <ButtonUpload
          className={`${props.theme ?? theme} ${fileName && 'fill'}`}
          onClick={() => {
            if (!dispatchEvent.event) hiddenFileInput.current.click()
            dispatchEvent.stopPropagation(false)
          }}
          disabled={props.disabled}
          data-qa={props['data-qa']}
        >
          {!Boolean(fileName) && <Icon name={iconName} />}
          <Label>{fileName || props.labelDefault || LABEL_DEFAULT}</Label>

          <input
            type="file"
            ref={hiddenFileInput}
            onChange={handleChange}
            style={{ display: 'none' }}
            accept={props.accept && props.accept.toString()}
            data-qa={props['data-qa'] && `${props['data-qa']}-input`}
          />

          <ButtonsContainer>
            {Boolean(value) && Boolean(fileName) && (
              <Tooltip title="Baixar">
                <ButtonDownload
                  href={value}
                  target="_blank"
                  data-qa={props['data-qa'] && `${props['data-qa']}-download`}
                  onClick={(evt) => {
                    if (props.onDownloadFile) {
                      props.onDownloadFile(value)
                      evt.preventDefault()
                    }

                    dispatchEvent.stopPropagation(true)
                  }}
                >
                  <IconContainer>
                    <Icon name="file_download_outlined" />
                  </IconContainer>
                </ButtonDownload>
              </Tooltip>
            )}
            {Boolean(fileName) && isRemovable && (
              <Tooltip title="Excluir">
                <IconContainer
                  data-qa={props['data-qa'] && `${props['data-qa']}-remove`}
                  onClick={(evt) => {
                    if (isRemovable) {
                      clearInput()
                      evt.preventDefault()
                    }

                    dispatchEvent.stopPropagation(true)
                  }}
                >
                  <Icon name="close" />
                </IconContainer>
              </Tooltip>
            )}
          </ButtonsContainer>
        </ButtonUpload>
      </ButtonContainer>

      {showHint && (
        <Hint className={`${props.theme ?? theme}`}>{hintText}</Hint>
      )}
    </>
  )
}

FileInput.defaultProps = {
  disabled: false,
}

export default FileInput

function shouldShowHint(hasError: boolean, hint: string) {
  return hasError || hint
}

function composeHintText(error: Error, hint: string): string {
  return error ? `${error.message} ${hint} ` : hint
}

function composeIconName(theme: string, isDisabled: boolean) {
  return theme === 'complete' || isDisabled ? 'insert_drive_file' : 'publish'
}

export function composeMaxFileSize(sizeInMb: number): number {
  const BYTES_TO_MB_FACTOR = 1048576
  return sizeInMb * BYTES_TO_MB_FACTOR
}

export function shouldValidate(
  file: File,
  maxFileSizeInBytes: number,
  accept?: string | string[],
): boolean {
  const fileExtention = `.${file?.name
    ?.split('.')
    ?.slice(-1)}`.toLocaleLowerCase()
  const extentions = accept && typeof accept === 'string' ? [accept] : accept

  const isValidSize = file?.size < maxFileSizeInBytes
  const isValidExtention = !extentions || extentions.includes(fileExtention)

  return isValidExtention && isValidSize
}

export function composeTheme(hasError: boolean, hasfile: boolean): string {
  if (hasError) return 'negative'
  if (hasfile) return 'complete'

  return 'enabled'
}

export type TTheme = 'complete' | 'enabled' | 'negative'
